const SETTLE_TOLERANCE = 6;
const SETTLE_FALLBACK_MS = 1400;

function prefersInstantScroll() {
  return matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'paused';
}

function targetOffset(target: HTMLElement) {
  const rootPadding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  const targetMargin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  return rootPadding + targetMargin;
}

function scrollTopFor(target: HTMLElement) {
  const maxScroll = document.documentElement.scrollHeight - innerHeight;
  const desiredTop = target.getBoundingClientRect().top + scrollY - targetOffset(target);
  return Math.max(0, Math.min(maxScroll, desiredTop));
}

function focusWithoutScrolling(target: HTMLElement) {
  if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea')) {
    target.setAttribute('tabindex', '-1');
    target.dataset.anchorFocus = '';
  }
  target.focus({ preventScroll: true });
}

/**
 * Scrolls a section into view below the floating header, re-aims once the scroll settles so late layout
 * shifts (pinned sections, closing menus) cannot leave it misplaced, then moves focus to it.
 * Returns false when no element has the given id.
 */
export function scrollToAnchor(id: string, { smooth = true }: { smooth?: boolean } = {}) {
  const target = document.getElementById(decodeURIComponent(id));
  if (!target) return false;

  const behavior: ScrollBehavior = smooth && !prefersInstantScroll() ? 'smooth' : 'instant';
  window.scrollTo({ top: scrollTopFor(target), behavior });
  focusWithoutScrolling(target);
  if (behavior === 'instant') return true;

  let settled = false;
  const settle = () => {
    if (settled) return;

    settled = true;
    window.removeEventListener('scrollend', settle);
    const correctedTop = scrollTopFor(target);
    if (Math.abs(correctedTop - scrollY) > SETTLE_TOLERANCE) {
      window.scrollTo({ top: correctedTop, behavior: 'smooth' });
    }
  };
  window.addEventListener('scrollend', settle);
  setTimeout(settle, SETTLE_FALLBACK_MS);
  return true;
}

/**
 * Returns the in-page anchor id a click should handle without a router navigation, or undefined.
 */
export function samePageAnchorId(event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0) return undefined;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return undefined;

  const link = (event.target as Element | null)?.closest?.('a[href]');
  if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) {
    return undefined;
  }

  const destination = new URL(link.href, location.href);
  const samePage = destination.origin === location.origin && destination.pathname === location.pathname;
  if (!samePage || !destination.hash || destination.hash === '#') return undefined;
  if (destination.hash === '#main-content') return undefined;

  return destination.hash.slice(1);
}
